import { useState, useEffect } from 'react'
import MobileLayout from '../../components/MobileLayout'
import { employeeApi } from '../../lib/employeeApi'

interface Notif {
  id: string
  type: string
  title: string
  body: string
  isRead: boolean
  createdAt: string
}

const TYPE_ICONS: Record<string, string> = {
  shift_published:    '📅',
  absence_approved:   '✅',
  absence_refused:    '❌',
  document_added:     '📄',
  timesheet_ready:    '📋',
}

function timeAgo(iso: string) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000
  if (diff < 60) return 'À l\'instant'
  if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`
  if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
}

export default function MobileNotifications() {
  const [notifs, setNotifs] = useState<Notif[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    load()
  }, [])

  async function load() {
    setLoading(true)
    const r = await employeeApi.get('/employee/me/notifications')
    setNotifs(r.data)
    setLoading(false)
  }

  async function markRead(id: string) {
    await employeeApi.post(`/employee/me/notifications/${id}/read`)
    setNotifs(ns => ns.map(n => n.id === id ? { ...n, isRead: true } : n))
  }

  async function markAllRead() {
    await employeeApi.post('/employee/me/notifications/read-all')
    setNotifs(ns => ns.map(n => ({ ...n, isRead: true })))
  }

  const unread = notifs.filter(n => !n.isRead).length

  return (
    <MobileLayout>
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold text-gray-800">Notifications</h1>
            {unread > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{unread}</span>
            )}
          </div>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-sm text-blue-500 hover:text-blue-700">
              Tout lire
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-400">Chargement...</div>
        ) : notifs.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
            <div className="text-4xl mb-3">🔔</div>
            <div className="text-gray-500 font-medium">Aucune notification</div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            {notifs.map((n, i) => (
              <button
                key={n.id}
                onClick={() => !n.isRead && markRead(n.id)}
                className={`w-full text-left flex items-start gap-3 p-4 hover:bg-gray-50 transition-colors ${i > 0 ? 'border-t border-gray-100' : ''} ${!n.isRead ? 'bg-blue-50 hover:bg-blue-50/80' : ''}`}
              >
                <div className="text-2xl flex-shrink-0 mt-0.5">
                  {TYPE_ICONS[n.type] || '🔔'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-semibold ${n.isRead ? 'text-gray-700' : 'text-gray-900'}`}>{n.title}</div>
                  <div className={`text-sm mt-0.5 ${n.isRead ? 'text-gray-400' : 'text-gray-600'}`}>{n.body}</div>
                  <div className="text-xs text-gray-400 mt-1">{timeAgo(n.createdAt)}</div>
                </div>
                {!n.isRead && (
                  <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-2" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </MobileLayout>
  )
}
